import { useEffect, useState, type SyntheticEvent } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { readProblem } from '../lib/problem';
import { useCreateFormat } from '../model/useFormatLibrary';
import type { LibraryGroup } from '../model/types';

const KEY = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const positiveInteger = (value: string) => /^\d+$/.test(value) && Number(value) > 0;

interface NewFormatDialogProps {
  open: boolean;
  groups: LibraryGroup[];
  /** The group to start on; the first group when absent. */
  initialGroupKey?: string;
  onOpenChange: (open: boolean) => void;
  onCreated: (key: string) => void;
}

export function NewFormatDialog({
  open,
  groups,
  initialGroupKey,
  onOpenChange,
  onCreated,
}: NewFormatDialogProps) {
  const { t } = useTranslation('admin');
  const create = useCreateFormat();
  const [key, setKey] = useState('');
  const [groupKey, setGroupKey] = useState('');
  const [name, setName] = useState('');
  const [w, setW] = useState('');
  const [h, setH] = useState('');
  const [surface, setSurface] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKey('');
    setGroupKey(initialGroupKey ?? (groups.length > 0 ? groups[0].key : ''));
    setName('');
    setW('');
    setH('');
    setSurface('');
    setSubmitted(false);
  }, [open, groups, initialGroupKey]);

  const keyError = !KEY.test(key) ? t('admin.adFormats.newFormatDialog.keyInvalid') : undefined;
  const nameError = !name.trim() ? t('admin.adFormats.newFormatDialog.required') : undefined;
  const sizeError =
    !positiveInteger(w) || !positiveInteger(h)
      ? t('admin.adFormats.newFormatDialog.sizeInvalid')
      : undefined;

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (keyError || nameError || sizeError || !groupKey) return;
    try {
      await create.mutateAsync({
        key,
        groupKey,
        name: name.trim(),
        w: Number(w),
        h: Number(h),
        surface: surface.trim() || null,
      });
      onCreated(key);
      onOpenChange(false);
    } catch (error) {
      const problem = readProblem(error);
      notificationService.showError(
        problem.code === 'already_exists'
          ? t('admin.adFormats.errors.alreadyExists')
          : t('admin.adFormats.errors.generic'),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-md">
          <DialogHeader>
            <DialogTitle>{t('admin.adFormats.newFormat')}</DialogTitle>
            <DialogDescription>
              {t('admin.adFormats.newFormatDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="new-format-group">{t('admin.adFormats.fields.group')}</FieldLabel>
            <Select value={groupKey} onValueChange={setGroupKey}>
              <SelectTrigger id="new-format-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {groups.map((group) => (
                  <SelectItem key={group.key} value={group.key}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="new-format-key">{t('admin.adFormats.fields.key')}</FieldLabel>
            <Input
              id="new-format-key"
              value={key}
              onChange={(event) => setKey(event.target.value)}
            />
            <FieldDescription>{t('admin.adFormats.fields.keyHint')}</FieldDescription>
            {submitted && keyError && <FieldError>{keyError}</FieldError>}
          </Field>
          <Field>
            <FieldLabel htmlFor="new-format-name">{t('admin.adFormats.fields.name')}</FieldLabel>
            <Input
              id="new-format-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {submitted && nameError && <FieldError>{nameError}</FieldError>}
          </Field>
          <div className="grid grid-cols-2 gap-md">
            <Field>
              <FieldLabel htmlFor="new-format-w">{t('admin.adFormats.fields.width')}</FieldLabel>
              <Input
                id="new-format-w"
                inputMode="numeric"
                value={w}
                onChange={(event) => setW(event.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="new-format-h">{t('admin.adFormats.fields.height')}</FieldLabel>
              <Input
                id="new-format-h"
                inputMode="numeric"
                value={h}
                onChange={(event) => setH(event.target.value)}
              />
            </Field>
          </div>
          {submitted && sizeError && <FieldError>{sizeError}</FieldError>}
          <Field>
            <FieldLabel htmlFor="new-format-surface">
              {t('admin.adFormats.fields.surface')}
            </FieldLabel>
            <Input
              id="new-format-surface"
              value={surface}
              onChange={(event) => setSurface(event.target.value)}
            />
            <FieldDescription>{t('admin.adFormats.fields.surfaceHint')}</FieldDescription>
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('admin.adFormats.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t('admin.adFormats.saving')
                : t('admin.adFormats.newFormatDialog.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
